import React from 'react';
import { MapPin, Clock, ChevronRight } from 'lucide-react';
import { useParams, useHistory } from 'react-router-dom';

const STATUS_BADGE = {
  open:      'bg-sky-100 text-sky-700',
  disputed:  'bg-orange-100 text-orange-700',
  completed: 'bg-emerald-100 text-emerald-700',
  paid:      'bg-teal-100 text-teal-700',
  cancelled: 'bg-red-100 text-red-600',
  expired:   'bg-gray-100 text-gray-500',
  resolved:  'bg-purple-100 text-purple-700',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function CoverageBar({ pct = 0 }) {
  const p     = Math.min(Math.max(pct, 0), 100);
  const color = p === 100 ? 'bg-emerald-500' : p >= 50 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${p}%` }} />
      </div>
      <span className="tabular-nums text-[11px] text-gray-500">{Math.round(p)}%</span>
    </div>
  );
}

/**
 * GridView — card grid of work orders.
 * Entire card is clickable — no separate "View Details" button.
 */
const GridView = ({ data, getDeadlineText }) => {
  const { customerCompanyId } = useParams();
  const history = useHistory();

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {data.map((job) => {
        const pct         = job?.statistics?.overallCoverage ?? 0;
        const statusKey   = job?.status?.toLowerCase() || '';
        const badgeCls    = STATUS_BADGE[statusKey] || 'bg-gray-100 text-gray-500';
        const deadlineTxt = getDeadlineText(job?.shifts);
        const shiftCount  = job?.shifts?.length ?? 0;
        const totalStaff  = job?.statistics?.totalStaffRequired ?? 0;

        return (
          <div
            key={job._id}
            onClick={() => history.push(`/customer-company/${customerCompanyId}/jobs/${job._id}`)}
            className="group flex cursor-pointer flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            {/* Colour band — status-tinted top strip */}
            <div className={`h-1 w-full ${
              statusKey === 'open'      ? 'bg-sky-400'     :
              statusKey === 'completed' ? 'bg-emerald-400' :
              statusKey === 'disputed'  ? 'bg-orange-400'  :
              statusKey === 'paid'      ? 'bg-teal-400'    :
              statusKey === 'cancelled' ? 'bg-red-400'     : 'bg-gray-300'
            }`} />

            <div className="flex flex-1 flex-col p-5">
              {/* Top row: title + status badge */}
              <div className="mb-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900 leading-snug">
                    {job.location || job.formattedAddress || 'Work Order'}
                  </p>
                  <p className="mt-1 flex items-center gap-1 truncate text-xs text-gray-400">
                    <MapPin size={10} className="flex-shrink-0" />
                    {job.formattedAddress || job.city || '—'}
                  </p>
                </div>
                <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${badgeCls}`}>
                  {job.status ? job.status.charAt(0).toUpperCase() + job.status.slice(1) : '—'}
                </span>
              </div>

              {/* Service type pill */}
              {job.serviceType && (
                <span className="mb-3 w-fit rounded-md bg-[#404880]/8 px-2 py-0.5 text-[11px] font-medium text-[#404880]">
                  {job.serviceType}
                </span>
              )}

              {/* Stats row */}
              <div className="mb-4 grid grid-cols-3 gap-3 rounded-lg bg-gray-50 px-3 py-2.5">
                <div>
                  <p className="text-[10px] text-gray-400">Shifts</p>
                  <p className="text-sm font-semibold text-gray-900">{shiftCount}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Rate</p>
                  <p className="text-sm font-semibold text-gray-900">${job.hourlyPayRate ?? 0}/hr</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Deadline</p>
                  <p className="text-xs font-semibold text-gray-900 leading-tight flex items-center gap-0.5">
                    <Clock size={9} className="flex-shrink-0 text-gray-400" /> {deadlineTxt}
                  </p>
                </div>
              </div>

              {/* Coverage bar */}
              <div className="mb-1">
                <p className="mb-1 text-[10px] text-gray-400">Shift coverage</p>
                <CoverageBar pct={pct} />
              </div>

              {/* Footer: created + arrow */}
              <div className="mt-auto flex items-center justify-between pt-3">
                <p className="text-[11px] text-gray-400">Created {fmtDate(job.createdAt)}</p>
                <ChevronRight size={14} className="text-gray-300 transition-transform group-hover:translate-x-0.5" />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GridView;
